<template>
    <header
        class="md-header fashion"
        :class="[{ mobile: isMobileSize, hide: hide }, pageClass]"
        v-show="showHeader"
    >
        <div
            ref="nav"
            class="header-container"
            role="navigation"
            :class="{ nosticky: isDetail || (!sticky && !curOverlap) }"
            :style="{ color: mainMenuText.color }"
        >
            <div class="header-bg" :style="{ background: bgColor }"></div>
            <a class="logo" :href="link" rel="noopener noreferrer">
                <site-img
                    :src="logo || null"
                    :style="{ height: size / 100 + 'em' }"
                    :size="100"
                    dir="height"
                    @load="sizeChange(), mobileImgFontSizeRecalc()"
                    :alt="altText || defaultLogoAlt"
                ></site-img>
            </a>
            <a
                v-if="subLogo"
                class="logo"
                style="margin-left: 0"
                :href="subLogoLink"
                rel="noopener noreferrer"
            >
                <site-img
                    :src="subLogo || null"
                    :style="{ height: subLogoSize / 100 + 'em' }"
                    :size="100"
                    dir="height"
                    @load="sizeChange(), mobileImgFontSizeRecalc()"
                    :alt="subAltText || defaultSublogoAlt"
                ></site-img>
            </a>
            <div
                class="nav-container"
                :style="mainText"
                :class="{ show: sideMenuShow }"
            >
                <!-- head menu start -->
                <ul class="head-menu">
                    <menu-item
                        v-for="(menu, index) in outMoreMenus"
                        :key="menu.name + index"
                        :menu="menu"
                        :showMenuArrow="isMobileSize"
                        :arrowIcon="
                            isMobileSize
                                ? 'icon-expand'
                                : 'icon-fashion-down'
                        "
                    ></menu-item>
                    <template v-if="isMobileSize">
                        <li
                            v-if="showTranslatePlugin"
                            class="menu-item"
                            @click="showLangMenu"
                        >
                            <div class="menu-language">
                                <div class="item-name">
                                    <i class="iconfont icon-website3"></i>
                                    {{ $t('md-header.language') }}
                                </div>
                                <div class="item-label">
                                    <span v-html="currentLanguageItem.label" />
                                    <i class="iconfont icon-next-big"></i>
                                </div>
                            </div>
                        </li>
                        <li
                            v-if="showCurrency"
                            class="menu-item"
                            @click="showCurrencyMenu"
                        >
                            <div class="menu-currency">
                                <div class="item-name">
                                    <i class="iconfont icon-currency"></i>
                                    {{ $t('md-header.currency') }}
                                </div>
                                <div class="item-label">
                                    {{ currentCurrencyLabel }}
                                    <i class="iconfont icon-next-big"></i>
                                </div>
                            </div>
                        </li>
                        <closely-app-download useModule="md-header"/>
                    </template>
                    <li
                        class="menu-item more"
                        :class="{ 'has-child': moreShow }"
                        @mouseenter="calcMoreStyle"
                    >
                        <a
                            class="iconfont icon-more"
                            style="font-size: 1.33em"
                            role="button"
                            tabindex="0"
                            :aria-label="$t('md-header.more')"
                            rel="noopener noreferrer"
                        ></a>
                        <div class="submenu-container">
                            <div class="wrapper" ref="wrapper">
                                <ol
                                    class="submenu hidden-scrollbar"
                                    :style="moreStyle"
                                >
                                    <menu-item
                                        v-for="(menu, index) in moreMenus"
                                        :key="menu.name + index"
                                        :menu="menu"
                                        isMore
                                        :showMenuArrow="
                                            isMobileSize
                                        "
                                        :arrowIcon="
                                            isMobileSize
                                                ? 'icon-expand'
                                                : 'icon-fashion-down'
                                        "
                                    ></menu-item>
                                </ol>
                            </div>
                        </div>
                    </li>
                </ul>
                <!-- head menu end -->
                <!-- pc google translate start -->
                <template v-if="!isMobileSize">
                    <ul
                        v-if="showTranslatePlugin"
                        class="head-language-pc langauge"
                    >
                        <menu-item
                            :menu="translateMenu"
                            class="item-language language"
                        >
                            <template v-slot:first>
                                <span v-html="currentLanguageItem.label"></span>
                                <i class="iconfont icon-expand"></i>
                            </template>
                            <template v-slot:second="slotProps">
                                <span
                                    @click="selectLanguage(slotProps.row)"
                                    class="lang-list-item"
                                >
                                    <span>{{ slotProps.row.label }}</span>
                                </span>
                            </template>
                        </menu-item>
                    </ul>
                    <ul v-if="showCurrency" class="head-language-pc currency">
                        <menu-item
                            :menu="curencyMenu"
                            class="item-language currency"
                        >
                            <template v-slot:first>
                                <span>{{ currentCurrencyLabel }}</span>
                                <i class="iconfont icon-expand"></i>
                            </template>
                            <template v-slot:second="slotProps">
                                <span
                                    @click="selectCurrency(slotProps.row)"
                                    class="lang-list-item"
                                >
                                    <span
                                    >{{ slotProps.row.abbr }}({{
                                        slotProps.row.symbol
                                    }})-{{ slotProps.row.name }}</span
                                    >
                                    <em
                                        v-show="
                                            currentCurrency ==
                                                slotProps.row.abbr
                                        "
                                        class="iconfont icon-checked-light"
                                    ></em>
                                </span>
                            </template>
                        </menu-item>
                    </ul>
                </template>
                <!-- pc google translate end -->
                <!-- head account start -->
                <ul
                    class="head-account"
                    :class="{
                        'has-login': user
                    }"
                >
                    <li v-if="user" class="menu-item has-child">
                        <a
                            class="menu-user"
                            @click="hideMenu"
                            href="/profile"
                            rel="noopener noreferrer"
                        >
                            <span
                                class="menu-user-initial"
                                :style="{ background: userBackgroundColor }"
                            >{{ initialFirstName }}</span
                            >
                            <span
                                v-if="isMobileSize"
                                class="first-name"
                            >{{ user.firstName }}</span
                            >
                        </a>
                        <div class="wrapper">
                            <ol class="submenu">
                                <li class="menu-item">
                                    <a
                                        href="/profile?type=MyHomes"
                                        rel="noopener noreferrer"
                                    >{{ $t('md-header.myHomes') }}</a
                                    >
                                </li>
                                <li class="menu-item">
                                    <a
                                        href="/profile?type=MyTour"
                                        rel="noopener noreferrer"
                                    >{{ $t('md-header.myTour') }}</a
                                    >
                                </li>
                                <li class="menu-item">
                                    <a
                                        href="/profile?type=Favorites"
                                        rel="noopener noreferrer"
                                    >{{ $t('md-header.myFavorites') }}</a
                                    >
                                </li>
                                <li class="menu-item">
                                    <a
                                        href="/profile?type=Searches"
                                        rel="noopener noreferrer"
                                    >{{ $t('md-header.mySearches') }}</a
                                    >
                                </li>
                                <li class="menu-item">
                                    <a
                                        href="/profile?type=Account"
                                        rel="noopener noreferrer"
                                    >{{
                                        $t('md-header.accountSettings')
                                    }}</a
                                    >
                                </li>
                                <li v-if="user.isOwner" class="menu-item">
                                    <a
                                        href="/admin/home"
                                        rel="noopener noreferrer"
                                    >{{ $t('md-header.chimeCrm') }}</a
                                    >
                                </li>
                                <closely-app-download useModule="md-header" class="parent-dom-of-closely"/>
                                <li class="menu-item">
                                    <a
                                        @click.prevent="logout"
                                        rel="noopener noreferrer"
                                    >{{ $t('md-header.signOut') }}</a
                                    >
                                </li>
                            </ol>
                        </div>
                        <span
                            v-show="isMobileSize"
                            class="mobile-logout iconfont icon-exit"
                            @click="logout"
                        ></span>
                    </li>
                    <template v-else>
                        <li
                            class="menu-item item-regist"
                            role="button"
                            tabindex="0"
                            @click="register"
                            :style="{
                                borderColor: mainMenuText && mainMenuText.color
                            }"
                        >
                            {{ $t('md-header.register') }}
                        </li>
                        <li
                            class="menu-item item-login"
                            role="button"
                            tabindex="0"
                            @click="signIn"
                            :style="{
                                borderColor: mainMenuText && mainMenuText.color
                            }"
                        >
                            {{ $t('md-header.signIn') }}
                        </li>
                    </template>
                </ul>
                <!-- head account end -->
            </div>
            <!-- mobile language menu start -->
            <div
                class="nav-container language"
                :style="mainText"
                v-if="isMobileSize && showTranslatePlugin"
                :class="{ show: showMobileLangMenu }"
            >
                <ul class="head-language-mobile">
                    <menu-item
                        class="item-language language"
                        v-for="lang in translateMenu.items"
                        :key="lang.name"
                        :menu="{}"
                        :class="{ active: currentLanguage === lang.name }"
                    >
                        <template v-slot:first>
                            <span
                                class="lang-list-item"
                                @click="selectLanguage(lang)"
                            >
                                <span>{{ lang.label }}</span>
                            </span>
                        </template>
                    </menu-item>
                </ul>
            </div>
            <!-- mobile language menu end -->
            <div
                class="nav-container language"
                :style="mainText"
                v-if="isMobileSize && showCurrency"
                :class="{ show: showMobileCurrencyMenu }"
            >
                <ul class="head-language-mobile">
                    <menu-item
                        class="item-language currency"
                        v-for="currency in curencyMenu.items"
                        :key="currency.abbr"
                        :menu="{}"
                        :class="{ active: currentCurrency === currency.abbr }"
                    >
                        <template v-slot:first>
                            <span
                                class="lang-list-item"
                                @click="selectCurrency(currency)"
                            >
                                <span
                                >{{ currency.abbr }}({{
                                    currency.symbol
                                }})-{{ currency.name }}</span
                                >
                            </span>
                        </template>
                    </menu-item>
                </ul>
            </div>
            <div
                class="nav-mask"
                @click="closeAllMenu"
                @touchmove.self.prevent
            ></div>
            <div class="nav-mask-dark" v-show="showCloseIcon"></div>
            <!-- mobile icon start -->
            <div
                v-show="showCloseIcon"
                class="iconfont mobile-menu icon-close"
                @click="closeAllMenu"
            ></div>
            <div
                v-show="!showCloseIcon && isMobileSize"
                class="iconfont mobile-menu icon-menu"
                @click="showMenu()"
            ></div>
            <!-- mobile icon end -->
        </div>
        <div v-if="!curOverlap" class="header-placeholder">
            <div
                :style="{ height: overlapSize + 'em', 'min-height': minHeight }"
            ></div>
        </div>
        <schema-info :info="seoSchema" v-if="seoSchema" />
    </header>
</template>

<script>
import Common from '../index';
import util from '../../../common/util';
import global from '../../../common/global';
export default {
    mixins: [Common],
    data: function() {
        return {
            hide: false,
            navTop: true
        };
    },
    watch: {
        curOverlap() {
            this.init();
        },
        sticky() {
            this.init();
        }
    },
    mounted() {
        this.init();
    },
    unmounted() {
        this.curOverlap && global.scroll(this.pageScroll, true);
    },
    computed: {
        minHeight() {
            if (this.isMobileSize) {
                return '22px';
            }
            if (!this.mainMenuText?.fontSize) {
                return `30px`;
            }
            
            let fontsize = this.mainMenuText.fontSize.slice(0, -2);
            return Math.max(30, +fontsize + 10) + 'px';
        },
        curOverlap() {
            if (this.isMobileSize) {
                return false;
            } else {
                return this.isOverlap;
            }
        }
    },
    methods: {
        init() {
            global.scroll(this.pageScroll, true);
            if (this.curOverlap) {
                global.scroll(this.pageScroll);
            } else {
                this.hide = false;
            }
            if (this.sticky) {
                util.topFixedHeight = this.$refs.nav.offsetHeight ?? 0;
            } else {
                util.topFixedHeight = 0;
            }
        },
        pageScroll(scrollY) {
            if (this.sideMenuShow) {
                return false;
            }
            if (!isNaN(this.scrollY) && !this.sticky) {
                if (scrollY < this.scrollY || scrollY <= 0) {
                    this.hide = false;
                } else {
                    this.hide = true;
                }
            }
            this.scrollY = scrollY;
        }
    }
};
</script>
<style lang="scss">
@import './classic.scss';
</style>
